import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../api";

const VIS_CYCLE = ["shared_all", "shared_accelerator", "private"];

function visLabel(vis, t) {
  if (vis === "shared_accelerator") return t("team1v1.sharedAccelerator");
  if (vis === "private") return t("team1v1.private");
  return t("team1v1.shared");
}

function visNorm(vis) {
  if (vis === "shared_accelerator") return "shared_accelerator";
  if (vis === "private") return "private";
  return "shared_all";
}

export default function DuringNotes({ sessionId, user, t, lang, prompts = [], readOnly = false }) {
  const [notes, setNotes] = useState([]);
  const [body, setBody] = useState("");
  const [visibility, setVisibility] = useState("shared_all");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get(`/sessions/${sessionId}/notes`)
      .then((r) => setNotes(r.data || []))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  };

  useEffect(() => { load(); }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/sessions/${sessionId}/notes`, { body: body.trim(), visibility });
      setBody("");
      load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setBusy(false); }
  };

  const cycleVis = async (n) => {
    const cur = visNorm(n.visibility);
    const next = VIS_CYCLE[(VIS_CYCLE.indexOf(cur) + 1) % VIS_CYCLE.length];
    try {
      await api.put(`/sessions/${sessionId}/notes/${n.id}`, { body: n.body, visibility: next });
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (n) => {
    try {
      await api.delete(`/sessions/${sessionId}/notes/${n.id}`);
      load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const visTestId = (vis) => {
    if (vis === "shared_accelerator") return "note-vis-accelerator";
    if (vis === "private") return "note-vis-private";
    return "note-vis-shared";
  };

  return (
    <div className="tb-block tb-section-follow" data-testid="during-notes">
      <h3 style={{ marginTop: 0 }}>{t("team1v1.duringNotes")}</h3>
      <p className="tb-muted">{t("team1v1.duringNotesHint")}</p>
      {prompts.length > 0 && (
        <ul data-testid="during-note-prompts" style={{ margin: "0 0 12px", paddingLeft: 18 }}>
          {prompts.map((p) => (
            <li key={p.key} className="tb-muted">
              {p.label?.[lang] || p.label?.en || p.label?.fr || p.key}
            </li>
          ))}
        </ul>
      )}
      {err && <div className="tb-form-error">{err}</div>}
      <ul style={{ listStyle: "none", padding: 0, margin: "0 0 12px" }} data-testid="notes-list">
        {notes.length === 0 && <li className="tb-muted">{t("team1v1.noNotes")}</li>}
        {notes.map((n) => (
          <li key={n.id} className="tb-block" style={{ marginBottom: 8, padding: 10 }} data-testid={`note-${n.id}`}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
              <span className={`tb-badge ${visNorm(n.visibility) === "private" ? "muted" : "good"}`}>
                {visLabel(visNorm(n.visibility), t)}
              </span>
              <span className="tb-muted" style={{ fontSize: "0.75rem" }}>
                {n.created_at ? new Date(n.created_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-US") : ""}
              </span>
            </div>
            <p style={{ whiteSpace: "pre-wrap", margin: "8px 0 0" }}>{n.body}</p>
            {n.author_id === user.id && !readOnly && (
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                <button type="button" className="tb-ghost-btn" onClick={() => cycleVis(n)} data-testid={`note-toggle-${n.id}`}>
                  {t("team1v1.makeShared")}
                </button>
                <button type="button" className="tb-ghost-btn" onClick={() => remove(n)}>{t("settings.templates.delete")}</button>
              </div>
            )}
          </li>
        ))}
      </ul>
      {!readOnly && (
      <form onSubmit={add} data-testid="note-form">
        <textarea rows={3} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t("team1v1.notePlaceholder")} data-testid="note-body" />
        <div className="tb-note-vis" style={{ marginTop: 8 }}>
          {["shared_all", "shared_accelerator", "private"].map((v) => (
            <label key={v}>
              <input type="radio" name="note-vis" checked={visibility === v} onChange={() => setVisibility(v)} data-testid={visTestId(v)} />
              {visLabel(v, t)}
            </label>
          ))}
          <button type="submit" className="tb-primary-btn" disabled={busy || !body.trim()} data-testid="note-submit">
            {busy ? t("auth.loading") : t("team1v1.addNote")}
          </button>
        </div>
      </form>
      )}
    </div>
  );
}
